import React from 'react';
import SEOHelmet from '../components/SEOHelmet';

const About: React.FC = () => {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "name": "About 12and22",
    "description": "Learn about 12and22's mission to provide reliable, affordable web hosting and domain services",
    "url": "https://12and22.com/about"
  };

  return (
    <div className="min-h-screen bg-white">
      <SEOHelmet
        title="About Us - Your Trusted Hosting Partner | 12and22"
        description="Learn about 12and22's mission to provide reliable, affordable web hosting and domain services. Serving customers worldwide since our founding."
        keywords="about us, company information, hosting provider, our mission, our story, 12and22"
        canonicalUrl="https://12and22.com/about"
        structuredData={structuredData}
      />
      {/* Hero Section */}
      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            {/* Badge */}
            <div className="inline-flex items-center px-4 py-2 mb-8 bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-full text-gray-700">
              <span className="w-2 h-2 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full mr-3"></span>
              <span className="text-sm font-medium">Since 2008</span>
            </div>

            {/* Main Headline */}
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-black leading-tight mb-8 text-gray-900">
              About <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-yellow-500">12and22</span>
            </h1>

            {/* Subtitle */}
            <p className="text-xl text-gray-600 mb-10 leading-relaxed max-w-4xl mx-auto">
              Your trusted partner for reliable hosting solutions. We've been powering businesses 
              with enterprise-grade infrastructure and world-class support for over 15 years.
            </p>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="bg-gray-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left Column - Content */}
            <div>
              <div className="inline-flex items-center px-4 py-2 mb-6 bg-gradient-to-r from-orange-50 to-yellow-50 border border-orange-200 rounded-full">
                <span className="w-2 h-2 bg-gradient-to-r from-orange-400 to-yellow-500 rounded-full mr-3"></span>
                <span className="text-sm font-semibold text-gray-700">Our Story</span>
              </div>
              
              <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-6">
                Building the Future of <span className="text-primary-navy">Web Hosting</span>
              </h2>
              
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Founded in 2008, 12and22 has grown from a small hosting provider to a trusted partner 
                for over 5,000 websites worldwide. We believe in providing reliable, secure, and 
                scalable hosting solutions that help businesses thrive online.
              </p>

              {/* Stats */}
              <div className="grid grid-cols-2 gap-8">
                {[
                  { number: '5,000+', label: 'Websites Hosted', color: 'from-orange-400 to-yellow-500' },
                  { number: '15+', label: 'Years Experience', color: 'from-primary-navy to-primary-800' },
                  { number: '99.99%', label: 'Uptime SLA', color: 'from-orange-400 to-yellow-500' },
                  { number: '24/7', label: 'Expert Support', color: 'from-primary-navy to-primary-800' }
                ].map((stat, index) => (
                  <div key={index} className="text-center">
                    <div className={`text-3xl font-black bg-gradient-to-r ${stat.color} bg-clip-text text-transparent mb-2`}>
                      {stat.number}
                    </div>
                    <div className="text-sm text-gray-600">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right Column - Card */}
            <div>
              <div className="bg-gradient-to-br from-white via-gray-50 to-gray-100 border border-gray-200 rounded-2xl p-8 shadow-xl relative overflow-hidden">
                {/* Glossy overlay */}
                <div className="absolute inset-0 bg-gradient-to-br from-white/30 via-white/10 to-transparent rounded-2xl"></div>
                <div className="relative z-10">
                  <div className="w-16 h-16 bg-gradient-to-br from-orange-500 via-yellow-500 to-orange-600 rounded-2xl flex items-center justify-center mb-6 shadow-lg relative overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-br from-white/30 via-white/10 to-transparent rounded-2xl"></div>
                    <svg className="w-8 h-8 text-white relative z-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">Our Mission</h3>
                  <p className="text-gray-600 leading-relaxed mb-6">
                    To provide reliable, secure, and scalable hosting solutions that empower businesses 
                    to succeed online. We're committed to delivering exceptional service and support 
                    that exceeds expectations.
                  </p>
                  
                  <div className="flex items-center space-x-4">
                    <div className="flex space-x-1">
                      {[...Array(5)].map((_, i) => (
                        <svg key={i} className="w-4 h-4 text-yellow-400" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                        </svg>
                      ))}
                    </div>
                    <span className="text-sm font-semibold text-gray-900">4.9/5 Customer Rating</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-white via-gray-50 to-gray-100 border border-gray-200 rounded-2xl p-12 text-center shadow-xl relative overflow-hidden">
            {/* Glossy overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-white/30 via-white/10 to-transparent rounded-2xl"></div>
            <div className="relative z-10">
              <div className="inline-flex items-center px-4 py-2 mb-6 bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-full">
                <span className="w-2 h-2 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full mr-3"></span>
                <span className="text-sm font-semibold text-gray-700">Join Our Community</span>
              </div>
              
              <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-6">
                Ready to Get Started?
              </h2>
              
              <p className="text-xl text-gray-600 mb-10 max-w-3xl mx-auto">
                Join thousands of satisfied customers who trust 12and22 for their hosting needs.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <a
                  href="/hosting"
                  className="bg-gradient-to-br from-primary-navy via-primary-800 to-primary-900 text-white px-8 py-4 rounded-lg font-bold text-lg hover:from-primary-800 hover:via-primary-900 hover:to-primary-navy transition-all duration-200 shadow-lg relative overflow-hidden group"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-transparent to-transparent group-hover:from-white/30 transition-all duration-200 rounded-lg"></div>
                  <span className="relative z-10">View Our Services</span>
                </a>
                <a
                  href="/contact"
                  className="border-2 border-orange-400 text-orange-600 px-8 py-4 rounded-lg font-bold text-lg hover:bg-gradient-to-r hover:from-orange-400 hover:via-yellow-500 hover:to-orange-600 hover:text-white hover:border-transparent transition-all duration-200 shadow-lg relative overflow-hidden group"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-transparent group-hover:from-white/20 transition-all duration-200 rounded-lg"></div>
                  <span className="relative z-10">Contact Us</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;